/**
 * Reviews grid cards keep a customer photo when the review already has one.
 * Otherwise they show the lifestyle image (second gallery image), then the
 * first available variant image, then the default product image.
 */
(function () {
  const ROOT_SELECTOR = '.judgeme-reviews-showcase';
  const CARD_SELECTOR = '.jdgm-review-card';
  const CUSTOMER_PHOTO = /review-images\.judgeme|judgeme\.imgix|judge\.me/i;

  /** @type {Map<string, Promise<object | null>>} */
  const productRequests = new Map();
  /** @type {{ byAria: Map<string, string>, byBody: Map<string, string> } | null} */
  let reviewIndex = null;

  /**
   * @param {string} value
   * @returns {string}
   */
  function normalize(value) {
    return String(value || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * @param {object} data
   * @returns {{ byAria: Map<string, string>, byBody: Map<string, string> }}
   */
  function indexReviews(data) {
    const byAria = new Map();
    const byBody = new Map();
    const seen = new Set();

    /**
     * @param {object} review
     */
    function add(review) {
      const id = review.uuid || review.id;
      if (id && seen.has(id)) return;
      if (id) seen.add(id);

      const name = normalize(review.reviewer_name);
      const title = normalize(review.title);
      const rating = String(review.rating ?? '').replace(/\.0$/, '');
      const label = `review by ${name}: ${rating} stars. ${title}`;
      if (!byAria.has(label)) byAria.set(label, review.product_url);

      const body = normalize(review.body || review.body_html).slice(0, 80);
      if (body.length > 24 && !byBody.has(body)) byBody.set(body, review.product_url);
    }

    /**
     * @param {unknown} node
     */
    function walk(node) {
      if (!node || typeof node !== 'object') return;
      if (!Array.isArray(node) && node.product_url && (node.reviewer_name || node.title || node.body)) {
        add(node);
      }
      const children = Array.isArray(node) ? node : Object.values(node);
      children.forEach(walk);
    }

    walk(data);
    return { byAria, byBody };
  }

  /**
   * @returns {{ byAria: Map<string, string>, byBody: Map<string, string> } | null}
   */
  function getReviewIndex() {
    const data = window.jdgm?.data?.reviewsGridWidget;
    if (!data) return null;
    if (!reviewIndex) reviewIndex = indexReviews(data);
    return reviewIndex;
  }

  /**
   * @param {string} url
   * @returns {string | null}
   */
  function handleFromUrl(url) {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * @param {Element} card
   * @returns {string | null}
   */
  function productUrlForCard(card) {
    const index = getReviewIndex();
    if (!index) return null;

    const label = normalize(card.getAttribute('aria-label'));
    if (index.byAria.has(label)) return index.byAria.get(label) || null;

    const description = card.querySelector('.jdgm-review-card__details-description');
    const body = normalize(description ? description.textContent : '').slice(0, 80);
    if (body && index.byBody.has(body)) return index.byBody.get(body) || null;

    return null;
  }

  /**
   * @param {string} handle
   * @returns {Promise<object | null>}
   */
  function fetchProduct(handle) {
    if (productRequests.has(handle)) return productRequests.get(handle);

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    productRequests.set(handle, request);
    return request;
  }

  /**
   * Customer photos and other native review media stay on the card.
   * @param {Element} card
   * @returns {boolean}
   */
  function hasCustomerMedia(card) {
    const medias = card.querySelectorAll('.jdgm-review-card-media');
    for (const media of medias) {
      if (!media.hasAttribute('data-jm-product-media')) return true;
    }

    const images = card.querySelectorAll('img');
    for (const image of images) {
      if (!(image instanceof HTMLImageElement)) continue;
      if (image.closest('[data-jm-product-media]')) continue;
      const src = `${image.currentSrc || ''} ${image.getAttribute('src') || ''} ${image.getAttribute('data-src') || ''} ${image.getAttribute('srcset') || ''}`;
      if (CUSTOMER_PHOTO.test(src)) return true;
    }

    return false;
  }

  /**
   * @param {unknown} image
   * @returns {string | null}
   */
  function imageSource(image) {
    if (!image) return null;
    if (typeof image === 'string') return image || null;
    if (typeof image === 'object' && typeof image.src === 'string' && image.src) return image.src;
    return null;
  }

  /**
   * First available variant image. `featured_image.src` is the usual .js shape;
   * `featured_media.preview_image` is the other shape used by that payload.
   * @param {object | null} product
   * @returns {string | null}
   */
  function variantImageUrl(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((item) => item && item.available) || variants[0];
    if (!variant) return null;

    const fromFeaturedImage = imageSource(variant.featured_image);
    if (fromFeaturedImage) return fromFeaturedImage;

    const media = variant.featured_media;
    return imageSource(media?.preview_image) || imageSource(media);
  }

  /**
   * @param {object | null} product
   * @returns {string | null}
   */
  function preferredImageUrl(product) {
    if (!product) return null;

    const images = Array.isArray(product.images) ? product.images : [];
    const lifestyle = imageSource(images[1]);
    if (lifestyle) return absoluteUrl(lifestyle);

    const variantImage = variantImageUrl(product);
    if (variantImage) return absoluteUrl(variantImage);

    const fallback = imageSource(images[0]);
    if (fallback) return absoluteUrl(fallback);
    return null;
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  function absoluteUrl(url) {
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  /**
   * @param {Element} card
   * @param {string} url
   * @param {string} alt
   */
  function applyImage(card, url, alt) {
    if (hasCustomerMedia(card)) return;

    let media = card.querySelector('[data-jm-product-media]');
    let image = media instanceof Element ? media.querySelector('img') : null;
    if (!(image instanceof HTMLImageElement)) {
      media = document.createElement('div');
      media.className = 'jdgm-review-card-media';
      media.setAttribute('data-jm-product-media', 'true');
      image = document.createElement('img');
      image.className = 'jdgm-review-card-media__image';
      image.alt = alt;
      media.appendChild(image);
      card.insertBefore(media, card.firstChild);
    }

    if (image.getAttribute('data-jm-product-src') === url) return;

    image.alt = alt;
    image.removeAttribute('srcset');
    image.setAttribute('data-jm-product-src', url);
    image.src = url;
  }

  /**
   * @param {Element} card
   */
  async function enhanceCard(card) {
    if (hasCustomerMedia(card)) return;

    const injected = card.querySelector('[data-jm-product-media] img');
    if (
      injected instanceof HTMLImageElement &&
      injected.getAttribute('data-jm-product-src') &&
      (injected.currentSrc || injected.src)
    ) {
      return;
    }

    const productUrl = productUrlForCard(card);
    const handle = handleFromUrl(productUrl);
    if (!handle) return;

    const product = await fetchProduct(handle);
    if (hasCustomerMedia(card)) return;

    const url = preferredImageUrl(product);
    if (!url) return;
    applyImage(card, url, product?.title || '');
  }

  /**
   * @param {ParentNode} root
   */
  function enhance(root) {
    if (!getReviewIndex()) return;
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      enhanceCard(card);
    });
  }

  function init() {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) return;

    roots.forEach((root) => {
      enhance(root);
      const observer = new MutationObserver(() => enhance(root));
      observer.observe(root, { childList: true, subtree: true });
    });

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      roots.forEach((root) => enhance(root));
      if (getReviewIndex() || tries > 40) window.clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
